import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import styles from "./Drawer.module.css";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Side the drawer slides in from. Defaults to "left". */
  side?: "left" | "right";
  /** Accessible label for the dialog region. */
  ariaLabel: string;
  /** Optional element ID to label the dialog. Use either `ariaLabel` or `ariaLabelledBy`. */
  ariaLabelledBy?: string;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
  );
}

/**
 * Slide-in panel for primary navigation on phone + tablet. Behavior is
 * deliberately a near-twin of Modal — same focus trap, escape handling,
 * focus restoration — so users get one consistent dialog feel and we
 * don't fork accessibility patches.
 *
 * Differences from Modal:
 *  - slides from the left/right edge instead of fading center
 *  - body scroll is locked while open (Modal stays inside the viewport
 *    anyway; the drawer's full-height surface invites scroll bleed-through)
 *  - mounted via portal so it can escape AppShell's overflow-clipped
 *    flex container
 *  - takes an `ariaLabel` instead of a `title` since drawers don't have
 *    visible titles (the brand mark inside the drawer body plays that role)
 */
export function Drawer({
  open,
  onClose,
  children,
  side = "left",
  ariaLabel,
  ariaLabelledBy,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    triggerRef.current = previouslyFocused;

    // Lock body scroll while open. Restore the prior value (which may
    // itself be an empty string set by user CSS) on cleanup so we don't
    // clobber any concurrent overflow management.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const root = panelRef.current;
      if (!root) return;
      const focusables = getFocusable(root);
      if (focusables.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !root.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const focusables = getFocusable(root);
      if (focusables.length > 0) {
        focusables[0]!.focus();
      } else {
        root.focus();
      }
    }, 30);

    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      const trigger = triggerRef.current;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
      triggerRef.current = null;
    };
  }, [open, onClose]);

  // Portal target — body, except during SSR / jsdom-without-body where we
  // bail and render nothing (matches React's portal behavior gracefully).
  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          className={styles.backdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14, ease: [0.2, 0, 0, 1] }}
        >
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            className={`${styles.panel} ${side === "right" ? styles.panelRight : styles.panelLeft}`}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            initial={{ x: side === "right" ? "100%" : "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: side === "right" ? "100%" : "-100%" }}
            transition={{ duration: 0.22, ease: [0.2, 0, 0, 1] }}
          >
            {children}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
